import { Api, CurrencyUtil, DateRange, DisplayType, DisplayValue, ModelRow, StringUtil } from "@mcleod/core";
import {
    ChangeEvent, CityState, Component, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent,
    DataSourceMode, ForcedCase, LookupModelSearchEvent, LookupModelSelectionEvent, Tab, Textbox
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { getDispatchControlBoolean } from "@mcleod/general";
import { CarrierQualification } from "./CarrierQualification";
import { AutogenLayoutLaneHistory } from "./autogen/AutogenLayoutLaneHistory";

export class LaneHistory extends AutogenLayoutLaneHistory {

    override onLoad() {
        this.defaultDateRange();
        this.setupCityState(this.citystateStopOriginCityId, this.switchSearchMode.checked);
        this.setupCityState(this.citystateStopDestCityId, this.switchSearchMode.checked);
        this.textboxOverridePayeeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true)
                this.payeeSelection(event.selection?.get("id"));
        });
        this.textboxCustomerId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                const customerId = event.selection.get("id");
                if (!StringUtil.isEmptyString(customerId))
                    this.sourceCustomer.search({ id: customerId });
                this.setSummaryPanel();
            }
        });
        this.textboxRadius.addChangeListener((event: ChangeEvent) => {
            this.citystateStopOriginCityId.textCity.required = this.citystateStopOriginCityId.textState.required = !StringUtil.isEmptyString(event.newValue);
        });
        this.styleTab(this.tabTotals);
        if (this.displayingFromToolbox) {
            this.layoutheaderLaneHist.visible = false;
            this.panelSummary.borderWidth = 0;
        }

        this.panelBuyToMarket.visible = getDispatchControlBoolean("show_btm", false);
        if (!this.panelBuyToMarket.visible) {
            const tableLaneHistory = this.layoutLaneHistoryTable.tableLaneHistory;
            tableLaneHistory.removeColumn(tableLaneHistory.columns.find(col => "columnHeaderBtm" === col.headingCell.id));
        }
        this.switchSearchMode.allowIndeterminate = false;
        this.switchSearchMode.checked = false;
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.setProps({ color: "subtle.light", paddingLeft: 10, borderWidth: 0 });
            tab.headingLabel.setProps({ color: "subtle.darker", fontSize: "large", fontBold: true })
        }
    }

    payeeSelection(payeeId: string) {
        (this.layoutQualifications as CarrierQualification).clearQualificationData();
        if (!StringUtil.isEmptyString(payeeId)) {
            Api.search("lme/powerbroker/carrier-qualifications", { "payee_id": payeeId }).then(result => {
                const carrierValidationData = result?.data[0];
                (this.layoutQualifications as CarrierQualification).setQualificationData(carrierValidationData);
                (this.layoutQualifications as CarrierQualification).textboxProfile.visible = false;
                (this.layoutQualifications as CarrierQualification).panelResults.borderRadius = 0;
                this.layoutQualifications.visible = true;
                this.setSummaryPanel();
            });
            this.callCarrierUnassignmentHistoryEndpoint();
            this.sourceCarrier.search({ id: payeeId });
        } else {
            this.setSummaryPanel();
        }
    }

    public defaultDateRange() {
        const dateRange = DateRange.parseDateRangeWithKeywords("t-365,t");
        this.textboxDateRange.text = DisplayValue.getDisplayValue(dateRange, DisplayType.DATERANGE, this.textboxDateRange.format);
    }

    private setupCityState(comp: CityState, isChecked: boolean, clearValues: boolean = false) {
        comp.dataSourceModeChanged(DataSourceMode.SEARCH);
        if (clearValues) {
            comp.textCity.text = comp.textZip.text = null;
        }
        comp.textCity.forcedCase = ForcedCase.UPPER;
        comp.textCity.addChangeListener((event: ChangeEvent) => {
            this.refreshLaneSummary(event.target as Component);
        });
        comp.textState.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            this.refreshLaneSummary(event.target);
        });
        comp.textZip.addChangeListener((event: ChangeEvent) => {
            this.refreshLaneSummary(event.target as Component);
        });

        if (isChecked) {
            comp.textState.caption = "State";
            comp.textZip.caption = "Zip";
        } else {
            if (comp.caption.startsWith("Origin")) {
                comp.textState.caption = "Origin State";
            } else if (comp.caption.startsWith("Dest")) {
                comp.textState.caption = "Destination State";
            }
            this.textboxRadius.text = comp.textCity.text = null;
        }
        comp.textCity.visible = comp.textZip.visible = this.textboxRadius.visible = isChecked;
        comp.width = isChecked ? 550 : null;
        comp.marginRight = !isChecked ? null : 30;
    }

    refreshLaneSummary(comp: Component) {
        const parent = comp.parent as CityState;
        let summaryComps = [];
        if (parent.caption.startsWith("Origin")) {
            summaryComps = [this.citystateOriginSummary, this.citystateOriginSummary2];
        } else if (parent.caption.startsWith("Dest")) {
            summaryComps = [this.citystateDestSummary, this.citystateDestSummary2];
        }
        summaryComps.forEach(summaryComp => {
            summaryComp.textCombined._captionLabel.color = "subtle.light";
            (summaryComp as CityState).displayComponentData(this.mainDataSource.getSearchFilter(), null, 0);
        })
        this.setSummaryPanel()
    }

    sourceLaneHistoryAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH && this.mainDataSource.data?.length > 0) {
            this.callCarrierUnassignmentHistoryEndpoint();
        }
        this.setTotalSummaryData();

        this.setupBuyToMarket();
    }

    sourceCarrierAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            if (!this.sourceCarrier?.activeRow?.data?.is_iso_active_and_licensed || (StringUtil.isEmptyString(this.sourceCarrier?.activeRow?.data?.carrier_scorecard_exported)
                || StringUtil.equalsIgnoreCase(this.sourceCarrier?.activeRow?.data?.carrier_scorecard_exported, "N"))) {
                this.panelIsoScorecardInfo.visible = false;
                return;
            }else{
                this.panelIsoScorecardInfo.visible = true;
            }

            if (!StringUtil.isEmptyString(this.sourceCarrier?.activeRow?.data?.kpi_score)) {
                this.labelIsoKpiScore.text = this.sourceCarrier?.activeRow?.data?.kpi_score;
                CommonTooltips.setTooltipContentLayout(this.labelIsoKpiScore, this.sourceCarrier?.activeRow?.data?.id, "lme/powerbroker/IsoScoreQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            } else {
                this.labelIsoKpiScore.text = "--";
            }
        }
    }

    setupBuyToMarket() {
        if (this.mainDataSource.activeRow && this.panelBuyToMarket.visible) {
            const modelRow = this.mainDataSource.activeRow;
            const btmUnderMarket = modelRow.isNull('btm_total') || !modelRow.getCurrency('btm_total').isPositive();
            const btmColor = btmUnderMarket ? 'success' : 'error';
            this.labelBuyToMarketTotal.color = btmColor;
            this.labelBuyToMarketTotalPercentCalc.color = btmColor;

            if (!btmUnderMarket) {
                this.labelBuyToMarketTotal.text = `OVER ${this.labelBuyToMarketTotal.text}`;
                this.labelBuyToMarketTotalPercentCalc.text = `OVER ${this.labelBuyToMarketTotalPercentCalc.text}`;
            }
        }
    }

    textboxCustomerIdOnChange(event: ChangeEvent) {
        if (StringUtil.isEmptyString(event.newValue)) {
            this.sourceLaneHistory.searchRow.set("customer.id", null);
            this.setSummaryPanel();
        } else if (event.domEvent?.type === "click") {
            const textbox = event.target as Textbox;
            this.sourceLaneHistory.searchRow.set("customer.id", textbox.getFirstLookupModelData().get("id"));
        }
    }

    async textboxOverridePayeeIdOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        if (StringUtil.isEmptyString(event.newValue)) {
            this.sourceLaneHistory.searchRow.set("override_payee_id", null);
            this.setSummaryPanel();
        } else if (event.domEvent?.type === "click") {
            this.sourceLaneHistory.searchRow.set("override_payee_id", textbox.getFirstLookupModelData().get("id"));
        }
    }

    setSummaryPanel() {
        const customerEntered = this.sourceLaneHistory.searchRow.get("customer.id") != null;
        const carrierEntered = this.sourceLaneHistory.searchRow.get("override_payee_id") != null;
        const laneEntered = this.citystateStopOriginCityId.textCity.text != ""
            || this.citystateStopOriginCityId.textState.text != ""
            || this.citystateStopOriginCityId.textZip.text != ""
            || this.citystateStopDestCityId.textCity.text != ""
            || this.citystateStopDestCityId.textState.text != ""
            || this.citystateStopDestCityId.textZip.text != "";

        this.labelSummary.visible = !carrierEntered && !customerEntered && !laneEntered;

        // Carrier Panels
        this.panelCarrierPrimary.visible = (carrierEntered && !customerEntered && !laneEntered);
        this.panelCarrierSecondary.visible = (carrierEntered && !laneEntered && customerEntered) || (carrierEntered && laneEntered && !customerEntered);
        this.panelCarrierThird.visible = carrierEntered && customerEntered && laneEntered;
        this.panelCarrierInfo.visible = carrierEntered;

        // Customer Panels
        this.panelCustomerPrimary.visible = customerEntered;

        // Lane Panels
        this.panelLanePrimary.visible = laneEntered && !customerEntered;
        this.panelLaneSecondary.visible = laneEntered && customerEntered;

        this.panelSecondarySummary.visible = this.panelLaneSecondary.visible || this.panelCarrierSecondary.visible;
    }

    labelCustomerNameOnDataDisplay(event: DataDisplayEvent) {
        this.setTooltipContent(this.labelCustomerNamePrimary, this.sourceLaneHistory.searchRow);
    }

    labelCarrierNameOnDataDisplay(event: DataDisplayEvent) {
        this.setTooltipContent(this.labelCarrierNamePrimary, this.sourceLaneHistory.searchRow);
        this.setTooltipContent(this.labelCarrierNameSecondary, this.sourceLaneHistory.searchRow);
        this.setTooltipContent(this.labelCarrierNameThird, this.sourceLaneHistory.searchRow);
    }

    // END - Expandable Filter Logic

    /** This is an event handler for the onChange event of switchSearchMode.  */
    switchSearchModeOnChange(event) {
        this.setupCityState(this.citystateStopOriginCityId, this.switchSearchMode.checked);
        this.setupCityState(this.citystateStopDestCityId, this.switchSearchMode.checked);
    }

    private setTooltipContent(comp: Component, modelRow: ModelRow) {
        if (comp.id == null) return;
        switch (comp.id) {
            case "labelCarrierName":
            case "labelCarrierNamePrimary":
            case "labelCarrierNameSecondary":
            case "labelCarrierNameThird":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("override_payee_id"), "lme/powerbroker/CarrierQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelCustomerName":
            case "labelCustomerNamePrimary":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("customer.id"), "lme/ar/CustomerQuickInfo", {
                    minWidth: 128, minHeight: 140
                });
            case "labelTotalCharge":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                    {
                        caption: "Freight Charge",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("prorated_move.prorated_revenue"))
                    },
                    {
                        caption: "Other Charges",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("prorated_move.prorated_oc"))
                    }]
                );
            case "labelTotalPay":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [{
                        caption: "Carrier Pay",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("override_pay_amt"))
                    },
                        {
                            caption: "Pay Adjustments",
                            displayType: DisplayType.CURRENCY,
                            text: CurrencyUtil.formatCurrency(modelRow.get("other_pay"))
                        }]
                );
        }
    }

    private setTotalSummaryData() {
        this.mainDataSource.activeRow?.set("margin_pay_percent", this.mainDataSource.summaryData.get("margin_pay_percent"));
        this.mainDataSource.activeRow?.set("margin_pay_total", this.mainDataSource.summaryData.get("margin_pay_total"));
        this.mainDataSource.activeRow?.set("move_count", this.mainDataSource.summaryData.get("move_count"));
        this.mainDataSource.activeRow?.set("total_pay", this.mainDataSource.summaryData.get("total_pay"));
        this.mainDataSource.activeRow?.set("total_pay_avg", this.mainDataSource.summaryData.get("total_pay_avg"));
        this.mainDataSource.activeRow?.set("move_charge_total", this.mainDataSource.summaryData.get("move_charge_total"));
        this.mainDataSource.activeRow?.set("move_charge_avg", this.mainDataSource.summaryData.get("move_charge_avg"));
        this.mainDataSource.activeRow?.set("carrier_pay_total", this.mainDataSource.summaryData.get("carrier_pay_total"));
        this.mainDataSource.activeRow?.set("carrier_pay_avg", this.mainDataSource.summaryData.get("carrier_pay_avg"));
        this.mainDataSource.activeRow?.set("other_charge_total", this.mainDataSource.summaryData.get("other_charge_total"));
        this.mainDataSource.activeRow?.set("other_charge_avg", this.mainDataSource.summaryData.get("other_charge_avg"));
        this.mainDataSource.activeRow?.set("freight_charge_total", this.mainDataSource.summaryData.get("freight_charge_total"));
        this.mainDataSource.activeRow?.set("freight_charge_avg", this.mainDataSource.summaryData.get("freight_charge_avg"));
        this.mainDataSource.activeRow?.set("btm_total", this.mainDataSource.summaryData.get("btm_total"));
        this.mainDataSource.activeRow?.set("btm_total_percent", this.mainDataSource.summaryData.get("btm_total_percent"));
    }

    private callCarrierUnassignmentHistoryEndpoint() {
        Api.search("lme/dispatch/carrier-unassignment-history", { "search_row": this.sourceLaneHistory.searchRow }).then(result => {
            const unAssignmentHistoryData = result?.data[0];
            this.labelAssignmentCount.text = unAssignmentHistoryData["total_assignments"];
            this.labelUnassignmentCount.text = unAssignmentHistoryData["num_unassignments"];
            this.labelPercentUnassigned.text = unAssignmentHistoryData["percent_unassignment"] + "%";
        });
    }

    textboxOrdersequipmentTypeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.applies_to = "L";
    }
}
